import type { AppRole } from '../features/auth/auth.types'

export interface AppRouteDefinition {
  path: string
  title: string
  eyebrow: string
  description: string
  icon: string
  audience: 'public' | 'auth' | AppRole
}

const route = (
  path: string,
  title: string,
  eyebrow: string,
  description: string,
  icon: string,
  audience: AppRouteDefinition['audience'],
): AppRouteDefinition => ({ path, title, eyebrow, description, icon, audience })

export const publicRoutes = [
  route('/contact', 'Contact', 'Chamber', 'Contact the doctor.', 'bi-envelope', 'public'),
  route('/', 'Doctor portfolio', 'Online chamber', 'Public doctor profile and appointment entry point.', 'bi-heart-pulse', 'public'),
  route('/appointment', 'Book an appointment', 'Online care', 'Choose a doctor and continue securely.', 'bi-calendar2-plus', 'public'),
  route('/about', 'About us', 'Public website', 'Information about the person or organization.', 'bi-building', 'public'),
  route('/services', 'Healthcare services', 'Public portfolio', 'Departments, services, consultation modes, and fees.', 'bi-bandaid', 'public'),
  route('/schedule', 'Consultation schedule', 'Public portfolio', 'Weekly availability and the next open appointment slots.', 'bi-calendar2-week', 'public'),
] as const

export const authRoutes = [
  route('/login', 'Patient login', 'Secure access', 'Authentication form integration will be completed in the auth phase.', 'bi-shield-lock', 'auth'),
  route('/register', 'Patient registration', 'Create an account', 'Patient onboarding fields and validation will be completed in the auth phase.', 'bi-person-plus', 'auth'),
] as const

export const patientRoutes = [
  route('/patient/dashboard', 'Patient dashboard', 'Patient portal', 'Appointments, care updates, and quick actions at a glance.', 'bi-grid-1x2', 'patient'),
  route('/patient/appointments', 'My appointments', 'Patient portal', 'Filter, review, and manage booked appointments.', 'bi-calendar2-check', 'patient'),
  route('/patient/appointments/new', 'Book an appointment', 'Patient portal', 'Choose a service, mode, date, and available slot.', 'bi-calendar2-plus', 'patient'),
  route('/patient/appointments/:appointmentId', 'Appointment details', 'Patient portal', 'Review appointment details, files, and actions.', 'bi-calendar2-check', 'patient'),
  route('/patient/files', 'Files and reports', 'Patient portal', 'Private medical documents and diagnostic reports.', 'bi-folder2-open', 'patient'),
  route('/patient/prescriptions', 'Prescriptions', 'Patient portal', 'Finalized prescriptions and downloadable documents.', 'bi-file-earmark-medical', 'patient'),
  route('/patient/prescriptions/:prescriptionId', 'Prescription details', 'Patient portal', 'Review a finalized prescription and its secure PDF.', 'bi-file-earmark-medical', 'patient'),
  route('/patient/payments', 'Payment history', 'Patient portal', 'Verified transactions, receipts, and payment status.', 'bi-credit-card', 'patient'),
  route('/patient/payments/:paymentId/demo', 'Demo payment', 'Patient portal', 'Demo checkout.', 'bi-credit-card', 'patient'),
  route('/patient/receipts/:receiptId', 'Receipt', 'Patient portal', 'Payment receipt.', 'bi-receipt', 'patient'),
  route('/patient/profile', 'Patient profile', 'Patient portal', 'Personal, contact, and notification preferences.', 'bi-person-circle', 'patient'),
] as const

export const doctorRoutes = [
  route('/doctor/dashboard', 'Doctor dashboard', 'Doctor workspace', 'Today\'s queue and clinical workload overview.', 'bi-grid-1x2', 'doctor'),
  route('/doctor/calendar', 'Appointment calendar', 'Doctor workspace', 'Calendar and list views of scheduled consultations.', 'bi-calendar3', 'doctor'),
  route('/doctor/appointments', 'Appointments', 'Doctor workspace', 'Patient cases, appointment status, and consultation access.', 'bi-calendar2-check', 'doctor'),
  route('/doctor/appointments/:appointmentId', 'Appointment details', 'Doctor workspace', 'Review the assigned patient case and status.', 'bi-calendar2-check', 'doctor'),
  route('/doctor/appointments/:appointmentId/consultation', 'Consultation workspace', 'Doctor workspace', 'Clinical notes, meeting controls, and prescribing.', 'bi-heart-pulse', 'doctor'),
  route('/doctor/availability', 'Availability', 'Doctor workspace', 'Recurring schedules, leave dates, and availability overrides.', 'bi-clock-history', 'doctor'),
  route('/doctor/prescriptions', 'Prescriptions', 'Doctor workspace', 'Draft, finalize, and review prescription versions.', 'bi-file-earmark-medical', 'doctor'),
  route('/doctor/prescriptions/:prescriptionId', 'Prescription details', 'Doctor workspace', 'Review prescription content and printable output.', 'bi-file-earmark-medical', 'doctor'),
] as const

export const adminRoutes = [
  route('/admin/dashboard', 'Admin dashboard', 'Administration', 'Operational appointments, patients, revenue, and payments.', 'bi-grid-1x2', 'admin'),
  route('/admin/departments', 'Departments', 'Administration', 'Clinical department configuration.', 'bi-diagram-3', 'admin'),
  route('/admin/services', 'Services', 'Administration', 'Service fees, durations, and availability.', 'bi-bandaid', 'admin'),
  route('/admin/users', 'Users', 'Administration', 'Patient, doctor, and administrator accounts.', 'bi-people', 'admin'),
  route('/admin/appointments', 'Appointments', 'Administration', 'Appointment oversight and manual administration.', 'bi-calendar2-check', 'admin'),
  route('/admin/payments', 'Payments', 'Administration', 'Transactions, receipts, and refund operations.', 'bi-credit-card', 'admin'),
  route('/admin/receipts/:receiptId', 'Receipt', 'Administration', 'Payment receipt.', 'bi-receipt', 'admin'),
  route('/admin/settings', 'Settings', 'Administration', 'Portfolio, chamber, booking, and branding settings.', 'bi-gear', 'admin'),
] as const

export const allRoutes: readonly AppRouteDefinition[] = [
  ...publicRoutes,
  ...authRoutes,
  ...patientRoutes,
  ...doctorRoutes,
  ...adminRoutes,
]
